import { useCallback, useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react';
import { DiagnosticReport, Patient } from '@medplum/fhirtypes';
import {
  PrenatalCheckFormState,
  UsePrenatalChecksResult,
} from '../utils/medical-history.types';
import {
  fetchPrenatalChecks,
  savePrenatalCheck,
  deletePrenatalCheck,
} from '../utils/prenatal-check.utils';

export const usePrenatalChecks = (
  patient: Patient | undefined,
  encounterId?: string | null
): UsePrenatalChecksResult => {
  const medplum = useMedplum();

  const [checks, setChecks] = useState<DiagnosticReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!patient) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const reports = await fetchPrenatalChecks(medplum, patient);
      setChecks(reports);
    } catch (err) {
      console.error('Error fetching prenatal checks:', err);
      setError(
        err instanceof Error ? err : new Error('Failed to fetch prenatal checks')
      );
    } finally {
      setLoading(false);
    }
  }, [medplum, patient]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addCheck = useCallback(
    async (formState: PrenatalCheckFormState): Promise<DiagnosticReport> => {
      if (!patient) {
        throw new Error('Patient is required');
      }

      const result = await savePrenatalCheck({
        medplum,
        patient,
        encounterId,
        formState,
      });

      await fetchData();
      return result;
    },
    [medplum, patient, encounterId, fetchData]
  );

  const updateCheck = useCallback(
    async (
      id: string,
      formState: PrenatalCheckFormState
    ): Promise<DiagnosticReport> => {
      if (!patient) {
        throw new Error('Patient is required');
      }

      const existingReport = checks.find((c) => c.id === id);
      if (!existingReport) {
        throw new Error('Report not found');
      }

      const result = await savePrenatalCheck({
        medplum,
        patient,
        encounterId,
        formState,
        existingReport,
      });

      await fetchData();
      return result;
    },
    [medplum, patient, encounterId, checks, fetchData]
  );

  const deleteCheck = useCallback(
    async (id: string): Promise<void> => {
      const report = checks.find((c) => c.id === id);
      if (!report) {
        throw new Error('Report not found');
      }

      await deletePrenatalCheck(medplum, report);
      await fetchData();
    },
    [medplum, checks, fetchData]
  );

  return {
    checks,
    loading,
    error,
    addCheck,
    updateCheck,
    deleteCheck,
    refetch: fetchData,
  };
};
